import { useState, useEffect, FC, CSSProperties } from 'react';
import cx from 'classnames';
import { useRecoilState, useRecoilValue } from 'recoil';
import { errorsAtom, processingAtom } from 'store';

import { useSound } from 'shared/hooks/useSound';
import { isMobile } from 'shared/helpers/isMobile';
import tile from 'assets/images/t.min.png';
import { IBlockButtonProps } from './types';
import {
  SButton,
  SInner,
  SFront,
  SBack,
  STile,
  SStrip,
  SBonusStrip,
} from './style';

const BlockButton: FC<IBlockButtonProps> = ({
  type,
  strip = 0,
  onClick,
  isSelected = false,
  isLast = false,
  disabled,
  isBonus = false,
  animDelay,
}) => {
  const isProcessing = useRecoilValue(processingAtom);

  const [playStoneClick] = useSound('stoneClick', { volume: 0.2 });
  const [playStoneHover] = useSound('stoneHover', { volume: 0.2 });
  const [playGoldReveal] = useSound('goldReveal', { volume: 0.2 });
  const [playDynamiteExplosion] = useSound('dynamiteExplosion', {
    volume: 0.4,
  });
  const [errors, setErrors] = useRecoilState(errorsAtom);

  const [isMobileDevice] = useState<boolean>(isMobile());
  const [isClicked, setIsClicked] = useState(false);

  const handleClick = () => {
    if (isProcessing) return;

    playStoneClick();
    setIsClicked(true);
    if (onClick) onClick();
  };

  const hoverHandle = () => {
    if (!isMobileDevice) playStoneHover();
  };

  useEffect(() => {
    setIsClicked(false);

    if (errors.items.length === 0) {
      if (type === 'regular' && isLast && isSelected) playGoldReveal();
      if (type === 'loss' && isLast && isSelected) playDynamiteExplosion();
    } else {
      setErrors(prev => ({
        ...prev,
        items: prev.items.filter(item => item !== 'BET_ERROR'),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, isLast, isSelected, errors.items, setErrors]);

  return (
    <SButton
      onMouseEnter={hoverHandle}
      onClick={handleClick}
      disabled={disabled || type !== 'block'}
      className={cx({
        'flip-card': true,
        'stone-shake-anim': isClicked,
        'is-last': isLast,
        'is-mobile': isMobileDevice,
        'is-gold': type === 'regular',
        'is-dynamite': type === 'loss',
        selected: isSelected,
      })}
    >
      <SInner
        className={cx({
          opened: type !== 'block',
        })}
        style={
          {
            '--flip-animation-delay': `${animDelay || 0}s`,
          } as CSSProperties
        }
      >
        <SFront>
          <img src={tile} alt="part" />
        </SFront>

        <SBack
          className={cx({
            selectedGold: isSelected && type === 'regular',
          })}
        >
          <STile
            className={cx({
              'is-gold': type === 'regular',
              'is-dynamite': type === 'loss',
              selected: isSelected,
            })}
          />
          {isBonus && <SBonusStrip />}
          {strip > 0 && (
            <SStrip>
              <span>&times;</span>
              <span>{strip}</span>
            </SStrip>
          )}
        </SBack>
      </SInner>
    </SButton>
  );
};

export default BlockButton;
